import { IconRefresh, IconSearch, IconX, IconTrash, IconDeviceFloppy, IconCamera, IconPhoto, IconQrcode, IconBarcode, IconKeyboard, IconEye, IconDiamond } from '@tabler/icons-react';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';
import { useEffect, useRef, useState, useMemo, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getAuthorizationHeader, http, imageBaseUrl } from '../../config/http';
import { CurrencyInput } from '../../utils/CurrencyInput';
import { DinamicTitle } from '../../utils/DinamicTitle';
import CetakStrokePembelian from './CetakStrokePembelian';
import '../../printStrokePembelian.css';
import LayoutsKasir from '../../layouts/LayoutsKasir';
import OpenSearchTrxJTrxB from './DetailScanTrxKasir/OpenSearchTrxJTrxB';
import ConfirmationPembelian from './ConfirmationPembelian';
import imageCompression from 'browser-image-compression';
import { Dialog, Transition } from '@headlessui/react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import useKarat from '../features/useKarat';

// ─── Scanner Modal ───────────────────────────────────────────────────────────
// ─── Scanner Modal (Support QR Code & Barcode 1D) ───────────────────────────
function ScannerModal({ isOpen, onClose, onScanSuccess }) {
  const scannerRef = useRef(null);
  const fileInputRef = useRef(null);
  const containerId = 'html5-pembelian-barcode-qr-scanner';
  const lastScanRef = useRef(0);
  const [isScanning, setIsScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const [showManual, setShowManual] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const [isFileScanning, setIsFileScanning] = useState(false);

  const playBeep = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1200, ctx.currentTime);
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.15);
    } catch {}
  };

  const destroyScanner = async () => {
    if (scannerRef.current) {
      try {
        if (scannerRef.current.isScanning) {
          await scannerRef.current.stop();
        }
        scannerRef.current.clear();
      } catch (err) {
        console.warn('Error closing scanner:', err);
      }
      scannerRef.current = null;
    }
    setIsScanning(false);
  };

  const startScanner = async () => {
    // Beri jeda singkat agar modal dan container DOM sudah ter-mount
    await new Promise((r) => setTimeout(r, 150));
    const container = document.getElementById(containerId);
    if (!container) return;

    try {
      await destroyScanner();

      const html5QrCode = new Html5Qrcode(containerId, {
        formatsToSupport: [
          Html5QrcodeSupportedFormats.QR_CODE,
          Html5QrcodeSupportedFormats.CODE_128,
          Html5QrcodeSupportedFormats.CODE_39,
          Html5QrcodeSupportedFormats.CODE_93,
          Html5QrcodeSupportedFormats.CODABAR,
          Html5QrcodeSupportedFormats.EAN_13,
          Html5QrcodeSupportedFormats.EAN_8,
          Html5QrcodeSupportedFormats.UPC_A,
          Html5QrcodeSupportedFormats.UPC_E,
          Html5QrcodeSupportedFormats.ITF,
          Html5QrcodeSupportedFormats.DATA_MATRIX,
        ],
        verbose: false,
      });

      scannerRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: 'environment' },
        {
          fps: 15,
          qrbox: (viewfinderWidth, viewfinderHeight) => {
            const w = Math.min(Math.floor(viewfinderWidth * 0.9), 320);
            const h = Math.min(Math.floor(viewfinderHeight * 0.65), 220);
            return { width: Math.max(w, 200), height: Math.max(h, 140) };
          },
          aspectRatio: 1.0,
        },
        (decodedText) => {
          const now = Date.now();
          if (now - lastScanRef.current < 1500) return;
          lastScanRef.current = now;
          playBeep();
          destroyScanner();
          onScanSuccess(decodedText);
          onClose();
        },
        () => {} // Abaikan per-frame parse error
      );

      setIsScanning(true);
      setScanError('');
    } catch (err) {
      console.error('Gagal memulai scanner:', err);
      setScanError('Kamera tidak dapat diakses atau izin kamera belum diberikan. Anda dapat menggunakan tombol Upload Foto atau Input Manual.');
      setIsScanning(false);
    }
  };

  const handleOpen = () => {
    if (!showManual) startScanner();
  };

  const handleClose = () => {
    destroyScanner();
    setShowManual(false);
    setManualInput('');
    setScanError('');
    onClose();
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualInput.trim()) {
      playBeep();
      onScanSuccess(manualInput.trim());
      setManualInput('');
      setShowManual(false);
      onClose();
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsFileScanning(true);
    setScanError('');
    try {
      let scanner = scannerRef.current;
      if (!scanner) {
        scanner = new Html5Qrcode(containerId, {
          formatsToSupport: [
            Html5QrcodeSupportedFormats.QR_CODE,
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.CODE_93,
            Html5QrcodeSupportedFormats.CODABAR,
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E,
            Html5QrcodeSupportedFormats.ITF,
          ],
          verbose: false,
        });
      }
      const decodedText = await scanner.scanFile(file, true);
      playBeep();
      await destroyScanner();
      onScanSuccess(decodedText);
      onClose();
    } catch (err) {
      console.warn('Gagal membaca gambar:', err);
      setScanError('Tidak terdeteksi QR Code maupun Barcode yang jelas pada foto tersebut. Silakan gunakan foto yang lebih fokus atau pilih Input Manual.');
    } finally {
      setIsFileScanning(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}
      afterEnter={handleOpen}
      beforeLeave={destroyScanner}
    >
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/75 backdrop-blur-sm" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto flex items-center justify-center p-4">
          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
            <Dialog.Panel className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border dark:border-slate-700/60 overflow-hidden text-left">
              <div className="px-5 py-3.5 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                <div>
                  <Dialog.Title className="font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2 text-sm sm:text-base">
                    <div className="flex items-center text-amber-500">
                      <IconQrcode className="w-5 h-5 -mr-1" />
                      <IconBarcode className="w-5 h-5" />
                    </div>
                    Scan Surat (QR Code & Barcode)
                  </Dialog.Title>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400">
                      ✓ QR Code
                    </span>
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-400">
                      ✓ Barcode 1D (Code 128 / Code 39 / EAN)
                    </span>
                  </div>
                </div>
                <button type="button" onClick={handleClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 transition-colors">
                  <IconX className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 sm:p-5">
                {!showManual ? (
                  <>
                    <div className="relative rounded-xl overflow-hidden bg-slate-950 aspect-square flex items-center justify-center border border-slate-200 dark:border-slate-800">
                      {/* Container kamera Html5Qrcode */}
                      <div
                        id={containerId}
                        className="w-full h-full [&_video]:w-full [&_video]:h-full [&_video]:object-cover [&_canvas]:absolute [&_canvas]:inset-0 [&_canvas]:w-full [&_canvas]:h-full [&_img]:hidden"
                      />

                      {/* Visual Viewfinder Target Frame */}
                      {isScanning && (
                        <div className="absolute inset-0 pointer-events-none flex items-center justify-center p-6">
                          <div className="w-full h-[65%] max-h-56 relative rounded-xl border border-amber-400/40">
                            {/* Sudut Frame */}
                            <div className="absolute -top-1 -left-1 w-6 h-6 border-t-4 border-l-4 border-amber-500 rounded-tl-lg" />
                            <div className="absolute -top-1 -right-1 w-6 h-6 border-t-4 border-r-4 border-amber-500 rounded-tr-lg" />
                            <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-4 border-l-4 border-amber-500 rounded-bl-lg" />
                            <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-4 border-r-4 border-amber-500 rounded-br-lg" />

                            {/* Laser Scanning Line */}
                            <div className="absolute inset-x-2 h-0.5 bg-gradient-to-r from-transparent via-amber-400 to-transparent shadow-[0_0_10px_#f59e0b] top-1/2 -translate-y-1/2 animate-pulse" />
                          </div>
                        </div>
                      )}

                      {isFileScanning && (
                        <div className="absolute inset-0 bg-black/75 flex flex-col items-center justify-center text-white z-20">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400 mb-2"></div>
                          <span className="text-xs font-semibold">Memindai foto surat...</span>
                        </div>
                      )}
                    </div>

                    {scanError && (
                      <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-xs text-red-600 dark:text-red-400">
                        {scanError}
                      </div>
                    )}

                    {/* Tombol Opsi: Upload File & Input Manual */}
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/*"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isFileScanning}
                        className="flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 font-medium text-xs sm:text-sm transition-colors cursor-pointer"
                      >
                        <IconPhoto className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                        <span>Upload Foto</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          destroyScanner();
                          setShowManual(true);
                        }}
                        className="flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 font-medium text-xs sm:text-sm transition-colors cursor-pointer"
                      >
                        <IconKeyboard className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                        <span>Input Manual</span>
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mb-3">
                      Ketikkan nomor faktur penjualan atau kode barcode item dari surat:
                    </p>
                    <form onSubmit={handleManualSubmit} className="space-y-3">
                      <input
                        type="text"
                        value={manualInput}
                        onChange={(e) => setManualInput(e.target.value)}
                        placeholder="Contoh: FKDJ-20240101-001 atau Kode Barcode"
                        autoFocus
                        className="w-full h-11 px-4 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-4 focus:ring-amber-500/10 focus:border-amber-500 transition-all placeholder-slate-400"
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setShowManual(false);
                            setTimeout(startScanner, 100);
                          }}
                          className="flex-1 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs sm:text-sm font-medium transition-colors flex items-center justify-center gap-1.5"
                        >
                          <IconCamera className="w-4 h-4" /> Buka Kamera
                        </button>
                        <button
                          type="submit"
                          disabled={!manualInput.trim()}
                          className="flex-1 h-10 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-xs sm:text-sm font-bold transition-colors"
                        >
                          Cari Surat
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </div>
              <div className="px-5 py-3 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700">
                <p className="text-xs text-center text-slate-400 dark:text-slate-500">
                  Arahkan kamera ke QR Code atau Barcode pada surat kwitansi
                </p>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

// ─── Detail Transaksi Modal ──────────────────────────────────────────────────
function DetailTrxModal({ isOpen, onClose, trx, numberWithCommas }) {
  if (!trx) return null;
  const isPenjualan = trx.kode_trx?.substring(0, 4) === 'FKDJ';

  const getImageUrl = () => {
    const gRaw = isPenjualan 
      ? (trx.item?.gambar1 || trx.item?.gambar) 
      : (trx.gambar || trx.item?.gambar || trx.gambar1 || trx.item?.gambar1);
      
    if (!gRaw) return null;
    
    let imgObj = null;
    try {
      if (Array.isArray(gRaw)) {
        imgObj = gRaw[0];
      } else if (typeof gRaw === 'object') {
        imgObj = gRaw;
      } else if (typeof gRaw === 'string') {
        const parsed = JSON.parse(gRaw);
        imgObj = Array.isArray(parsed) ? parsed[0] : parsed;
      }
    } catch {
      imgObj = null;
    }
    
    const path = imgObj?.asli || imgObj?.thumbnail;
    return path ? `${imageBaseUrl}${path}` : null;
  };

  const imgSrc = getImageUrl();

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto flex items-center justify-center p-4">
          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-slate-900 border dark:border-slate-700/60 p-6 text-left align-middle shadow-2xl transition-all">
              <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3 mb-4">
                <Dialog.Title className="font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2 text-lg">
                  <IconEye className="w-5 h-5 text-emerald-500" />
                  Detail Transaksi Hasil Scan
                </Dialog.Title>
                <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 transition-colors">
                  <IconX className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-3.5">
                <div className="flex justify-between items-center bg-slate-50 dark:bg-slate-800/40 p-3.5 rounded-xl border dark:border-slate-800">
                  <span className="text-xs text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider">No. Faktur</span>
                  <span className="text-sm font-extrabold text-emerald-600 dark:text-emerald-400 font-mono">{trx.kode_trx}</span>
                </div>

                <div className="divide-y divide-slate-100 dark:divide-slate-800/50">
                  <div className="py-2.5 flex justify-between text-sm">
                    <span className="text-slate-500 dark:text-slate-400">Tanggal</span>
                    <span className="font-semibold text-slate-800 dark:text-slate-200">{trx.created_at || '-'}</span>
                  </div>
                  <div className="py-2.5 flex justify-between text-sm">
                    <span className="text-slate-500 dark:text-slate-400">Jenis Barang</span>
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      {isPenjualan ? trx.item?.jenis_kategori?.nama_jenis : trx.jenis_kategori?.nama_jenis}
                    </span>
                  </div>
                  <div className="py-2.5 flex justify-between text-sm">
                    <span className="text-slate-500 dark:text-slate-400">Berat Item</span>
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      {isPenjualan ? trx.item?.berat : trx.berat} gram
                    </span>
                  </div>
                  <div className="py-2.5 flex justify-between text-sm">
                    <span className="text-slate-500 dark:text-slate-400">Emas / Karat</span>
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      {isPenjualan ? trx.item?.karat : trx.karat} karat
                    </span>
                  </div>
                  <div className="py-2.5 flex justify-between text-sm">
                    <span className="text-slate-500 dark:text-slate-400">Total Harga</span>
                    <span className="font-bold text-emerald-600 dark:text-emerald-400">
                      Rp {numberWithCommas(trx.total_harga || 0)}
                    </span>
                  </div>
                  <div className="py-2.5 flex justify-between text-sm">
                    <span className="text-slate-500 dark:text-slate-400">Harga Modal / gram</span>
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      Rp {numberWithCommas(isPenjualan ? (trx.item?.harga_modal || 0) : (trx.harga_modal || 0))}
                    </span>
                  </div>
                  <div className="py-2.5 flex justify-between text-sm">
                    <span className="text-slate-500 dark:text-slate-400">Total Harga Modal</span>
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      Rp {numberWithCommas(
                        isPenjualan
                          ? Math.round((trx.item?.harga_modal || 0) * (trx.item?.berat || 0))
                          : Math.round((trx.harga_modal || 0) * (trx.berat || 0))
                      )}
                    </span>
                  </div>
                  {trx.laba !== undefined && trx.laba !== null && (
                    <div className="py-2.5 flex justify-between text-sm">
                      <span className="text-slate-500 dark:text-slate-400">Untung / Rugi Laba</span>
                      <span className={`font-bold py-0.5 px-2 rounded text-xs ${trx.laba >= 0
                        ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30'
                        : 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30'
                        }`}>
                        {trx.laba >= 0 ? 'Untung (+)' : 'Rugi (-)'} Rp {numberWithCommas(Math.abs(Math.round(trx.laba)))}
                      </span>
                    </div>
                  )}
                  <div className="py-2.5 flex justify-between text-sm">
                    <span className="text-slate-500 dark:text-slate-400">Toko / Cabang Asal</span>
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      {trx.item?.cabang?.nama_cabang || trx.user?.cabang?.nama_cabang || '-'}
                    </span>
                  </div>
                </div>

                {imgSrc && (
                  <div className="mt-4 pt-3.5 border-t border-slate-100 dark:border-slate-800/50 flex flex-col items-center">
                    <span className="text-xs text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider block self-start mb-2">Foto Barang / Surat</span>
                    <div className="relative group">
                      <PhotoProvider>
                        <PhotoView src={imgSrc}>
                          <img
                            src={imgSrc}
                            alt="Foto Barang"
                            className="h-28 w-28 rounded-xl border border-slate-200 dark:border-slate-700/60 object-cover bg-white dark:bg-slate-900 cursor-pointer shadow-sm hover:scale-105 transition duration-200"
                            title="Klik untuk memperbesar gambar"
                          />
                        </PhotoView>
                      </PhotoProvider>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-800/80 flex justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-sm font-bold rounded-xl transition active:scale-95"
                >
                  Tutup
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

export default function Pembelian() {
  const [kategori, setKategori] = useState([]);
  const [loading, setLoading] = useState(false);
  const [harga_beli, setHargaBeli] = useState(0);
  const [selectImage, setSelectImage] = useState([]);
  const [formData, setFormData] = useState({
    jenis_kategori_id: '',
    kondisi_barang: '',
    berat: '',
    karat: '',
    surat: '',
    keterangan: '',
    kode_item: '',
  });
  const [scannerOpen, setScannerOpen] = useState(false);
  const [isReadOnlyKodeItem, setIsReadOnlyKodeItem] = useState(true);
  const [scannedTrxDetails, setScannedTrxDetails] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [kategoriId, setKategoriId] = useState('');
  const [gambar, setGambar] = useState('');
  const [isOpenKaratModal, setIsOpenKaratModal] = useState(false);
  const [isOpenJenisModal, setIsOpenJenisModal] = useState(false);
  const [searchJenis, setSearchJenis] = useState('');

  const [dataPrintPembelian, setDataPrintPembelian] = useState([]);
  // console.log('dataPrintPembelian::: ', dataPrintPembelian);

  const [scanKode, setScanKode] = useState('');
  const [dataScanKode, setDataScanKode] = useState([]);
  // console.log('dataScanKode::: ', dataScanKode);
  const [msg, setMsg] = useState('');

  const { jenis_kategori_id, kondisi_barang, karat, berat, surat, keterangan, kode_item } = formData;

  const formRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const regex = /^\d*\.?\d{0,3}$/;
    if (formData.berat && !regex.test(formData.berat)) {
      setFormData((prev) => ({
        ...prev,
        berat: prev.berat.slice(0, -1),
      }));
    }
  }, [formData.berat]);

  function handleOnChange(e) {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  }

  useEffect(() => {
    async function getKategori() {
      await http
        .get('/kategori', {
          headers: { Authorization: getAuthorizationHeader() },
        })
        .then((res) => {
          setKategori(res.data);
        })
        .catch((err) => {
          if (err.response === 'undefined') {
            return null;
          } else if (err.response.status === 401) {
            navigate('/login');
          }
        });
    }
    getKategori();
  }, [navigate]);

  function resetForm() {
    setFormData({
      jenis_kategori_id: '',
      kondisi_barang: '',
      berat: '',
      karat: '',
      surat: '',
      keterangan: '',
      kode_item: '',
    });
    setKategoriId('');
    setHargaBeli(0);
    setSelectImage([]);
    setGambar('');
    setJenisKategori([]);
    setIsReadOnlyKodeItem(true);
    setScannedTrxDetails(null);
    setSearchJenis('');
    setIsOpenJenisModal(false);
  }

  async function handleSubmitPembelian(e) {
    e.preventDefault();
    if (!jenis_kategori_id) {
      toast.warning('Pilih jenis kategori terlebih dahulu');
      return;
    }
    if (!karat) {
      toast.warning('Pilih karat terlebih dahulu');
      return;
    }
    const dataPembelian = {
      jenis_kategori_id,
      kondisi_barang,
      karat,
      berat,
      surat,
      harga_beli,
      keterangan,
      gambar,
      kode_item,
    };
    // console.log(dataPembelian);
    setLoading(true);
    try {
      setLoading(true);
      const response = await http.post(`pembelian`, dataPembelian, {
        headers: { Authorization: getAuthorizationHeader() },
      });
      setDataPrintPembelian(response.data.data);
      resetForm();
      setLoading(false);
      openModal();
      toast.success(response.data.message, { autoClose: 600, });
      return response.data.data;
    } catch (err) {
      if (err.code === 'ERR_NETWORK') {
        toast.warning('Periksa jaringan internet', { autoClose: 600, });
      } else if (err.response.status === 422) {
        toast.warning('Lengkapi input data', { autoClose: 600, });
      } else if (err.response.status === 401) {
        navigate('/login');
      } else if (err.response.status === 500) {
        toast.warning('Terjadi kesalahan di server', { autoClose: 600, });
      }
    }
    setLoading(false);
  }
  DinamicTitle('Kasir Pembelian || Komaladewi');
  const [loadingGetDetailScan, setLoadingGetDetailScan] = useState(false);
  const [statusKode, setStatusKode] = useState('');

  async function handleScanSurat() {
    setLoadingGetDetailScan(true);
    await http
      .get(`penjualan/scan/${scanKode}`, {
        headers: { Authorization: getAuthorizationHeader() },
      })
      .then((res) => {
        setDataScanKode(res.data.data);
        setMsg(res.data.message);
      })
      .catch((err) => {
        if (err.response.status === 404) {
          setMsg(err.response.data.message);
          setStatusKode(err.response.status);
        }
      });
    setLoadingGetDetailScan(false);
  }

  async function lookupScannedInvoice(code) {
    if (!code?.trim()) return;
    const cleanCode = code.trim().replace(/^https?:\/\/[^/]+\/(?:kwitansi\/|scan\/)?/i, '').split('/').pop();
    setLoading(true);
    try {
      let res;
      try {
        res = await http.get(`penjualan/scan/${cleanCode}`, {
          headers: { Authorization: getAuthorizationHeader() },
        });
      } catch (scanErr) {
        // Fallback coba ke findfaktur jika tidak ditemukan di scan
        if (scanErr.response?.status === 404) {
          try {
            res = await http.get(`penjualan/findfaktur/${cleanCode}`, {
              headers: { Authorization: getAuthorizationHeader() },
            });
          } catch {
            throw scanErr;
          }
        } else {
          throw scanErr;
        }
      }

      const data = res.data?.data;

      if (data) {
        // We found the transaction! Let's fill the form!
        // 1. Set KategoriId
        const itemKategoriId = data.item?.jenis_kategori?.kategori_id || data.jenis_kategori?.kategori_id;
        if (itemKategoriId) {
          await SelectJenisKategori(itemKategoriId);
        }

        // 2. Set form values
        const asalCabang = data.item?.cabang?.nama_cabang || data.user?.cabang?.nama_cabang || data.cabang?.nama_cabang || '';
        const kodeTrx = data.kode_trx || '';
        const kodeItem = data.item?.kode_item || data.kode_item || '';

        setFormData({
          jenis_kategori_id: data.item?.jenis_kategori_id || data.jenis_kategori_id || '',
          kondisi_barang: data.item?.kondisi_barang || data.kondisi_barang || '',
          berat: data.item?.berat ? String(data.item.berat) : data.berat ? String(data.berat) : '',
          karat: data.item?.karat ? Number(data.item.karat) : data.karat ? Number(data.karat) : '',
          surat: '1', // default to "Ya" since we scanned the invoice
          keterangan: `toko asal ${asalCabang} trx = ${kodeTrx} dan item = ${kodeItem}`.trim(),
          kode_item: kodeItem,
        });
        setIsReadOnlyKodeItem(true);
        setScannedTrxDetails(data);

        const storeLabel = asalCabang ? ` (Toko Asal: ${asalCabang})` : '';
        toast.success(`Berhasil memuat item dari surat: ${kodeTrx}${storeLabel}`, { autoClose: 2000 });
      } else {
        toast.error('Kwitansi/Barang tidak ditemukan. Silakan isi data secara manual.', { autoClose: 3000 });
        setFormData((prev) => ({
          ...prev,
          kode_item: '',
        }));
        setIsReadOnlyKodeItem(false);
        setScannedTrxDetails(null);
      }
    } catch (err) {
      console.error(err);
      const errMsg = err.response?.data?.message || 'Kwitansi/Barang tidak ditemukan. Silakan isi data secara manual.';
      toast.error(errMsg, { autoClose: 3000 });
      setFormData((prev) => ({
        ...prev,
        kode_item: '',
      }));
      setIsReadOnlyKodeItem(false);
      setScannedTrxDetails(null);
    } finally {
      setLoading(false);
    }
  }

  // Support Hardware Barcode Scanner Gun (USB / Bluetooth)
  const lookupScannedInvoiceRef = useRef(lookupScannedInvoice);
  useEffect(() => {
    lookupScannedInvoiceRef.current = lookupScannedInvoice;
  });

  useEffect(() => {
    let buffer = '';
    let lastKeyTime = Date.now();

    const handleKeyDown = (e) => {
      // Abaikan jika fokus sedang berada pada input/textarea/select yang sedang diedit
      const activeEl = document.activeElement;
      const tag = activeEl?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
        return;
      }

      const currentTime = Date.now();
      // Hardware barcode scanner mengetik sangat cepat (< 120ms per char)
      if (currentTime - lastKeyTime > 120) {
        buffer = '';
      }
      lastKeyTime = currentTime;

      if (e.key === 'Enter') {
        const clean = buffer.trim();
        if (clean.length >= 3) {
          e.preventDefault();
          lookupScannedInvoiceRef.current?.(clean);
          buffer = '';
        }
      } else if (e.key.length === 1) {
        buffer += e.key;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const [jenisKategori, setJenisKategori] = useState([]);
  const [req, setReq] = useState({
    KategoriName: '',
    jenis_kategori_id: '',
  });

  async function SelectJenisKategori(idData) {
    setKategoriId(idData);
    await http
      .get(`/jenis/kategori/${idData}`, {
        headers: { Authorization: getAuthorizationHeader() },
      })
      .then((res) => {
        setJenisKategori(res.data.data);
      })
      .catch((err) => {
        console.log('error jenis kategori', err);
      });
  }

  const kondisiOption = [
    { label: 'Service', value: 'Service' },
    { label: 'Ready Jual', value: 'Ready Jual' },
    { label: 'Rongsok', value: 'Rongsok' },
  ];

  const suratOption = [
    { label: 'Ya', value: '1' },
    { label: 'Tidak', value: '0' },
  ];

  const { karatOption: rawKaratOption } = useKarat();

  const karatOption = useMemo(() => {
    let list = [];
    if (rawKaratOption && rawKaratOption.length > 0) {
      list = rawKaratOption
        .map((k) => (typeof k === 'object' && k !== null ? Number(k.karat) : Number(k)))
        .filter((n) => !isNaN(n));
    }
    if (list.length === 0) {
      list = [16, 17, 18, 19, 20, 21, 22, 23, 24];
    }
    if (formData.karat !== '' && !list.includes(Number(formData.karat))) {
      list = [...list, Number(formData.karat)].sort((a, b) => a - b);
    }
    return list;
  }, [rawKaratOption, formData.karat]);

  const isFormValid = useMemo(() => {
    return (
      kategoriId !== '' &&
      jenis_kategori_id !== '' &&
      kondisi_barang !== '' &&
      berat !== '' &&
      karat !== '' &&
      surat !== '' &&
      Number(harga_beli) > 0 &&
      selectImage.length > 0
    );
  }, [kategoriId, jenis_kategori_id, kondisi_barang, berat, karat, surat, harga_beli, selectImage]);

  useEffect(() => {
    if (scanKode === '' && scanKode.length < 10) {
      console.log('Silahkan Masukkan Kode untuk mencari barang');
    } else {
      handleScanSurat();
    }
  }, [scanKode, navigate, statusKode]);

  function numberWithCommas(x) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  let [isOpen, setIsOpen] = useState(false);

  function closeModal() {
    setIsOpen(false);
  }

  function openModal() {
    setIsOpen(true);
  }

  const handleImageChange = async (event) => {
    const selectedFiles = event.target.files;
    const selectedFilesArray = Array.from(selectedFiles);

    if (selectImage.length < 1) {
      const file = selectedFiles[0];

      // Log size before compression
      console.log('Ukuran file sebelum kompresi:', (file.size / 1024).toFixed(2), 'KB');

      // Options for image compression
      const options = {
        maxSizeMB: 1, // Maximum size in MB
        maxWidthOrHeight: 1024, // Maximum width or height
        useWebWorker: true, // Use web worker for better performance
      };

      try {
        // Compress the image
        const compressedFile = await imageCompression(file, options);

        // Log size after compression
        console.log('Ukuran file setelah kompresi:', (compressedFile.size / 1024).toFixed(2), 'KB');

        // Read the compressed image
        const reader1 = new FileReader();
        reader1.addEventListener('load', function () {
          setGambar(reader1.result.split(',')[1]);
        });
        reader1.readAsDataURL(compressedFile);

        // Update the selected image array with the compressed image
        const compressedImageURL = URL.createObjectURL(compressedFile);
        setSelectImage([compressedImageURL]);
      } catch (error) {
        console.error('Error compressing image:', error);
      }
    } else {
      // If more than one image is selected, handle accordingly
      const imagesArray = selectedFilesArray.map((file) => {
        return URL.createObjectURL(file);
      });
      setSelectImage(imagesArray);
    }

    // FOR BUG IN CHROME
    event.target.value = '';
  };

  // const handleImageChange = (event) => {
  //   const selectedFiles = event.target.files;
  //   const selectedFilesArray = Array.from(selectedFiles);

  //   if (selectImage.length < 1) {
  //     const reader1 = new FileReader();
  //     reader1.addEventListener('load', function () {
  //       setGambar(reader1.result.split(',')[1]);
  //     });
  //     reader1.readAsDataURL(selectedFiles[0]);
  //   }
  //   const imagesArray = selectedFilesArray.map((file) => {
  //     return URL.createObjectURL(file);
  //   });
  //   // setSelectImage((previousImages) => previousImages.concat(imagesArray));
  //   setSelectImage(imagesArray);
  //   // FOR BUG IN CHROME
  //   event.target.value = '';
  // };

  let [isOpenModalSearch, setIsOpenModalSearch] = useState(false);

  function closeModalSearch() {
    setIsOpenModalSearch(false);
    setScanKode('');
  }

  function openModalSearch() {
    setIsOpenModalSearch(true);
  }

  let [isOpenConfPembelian, setIsOpenConfPembelian] = useState(false);

  function closeModalConfPembelian() {
    setIsOpenConfPembelian(false);
  }

  function openModalConfPembelian() {
    setIsOpenConfPembelian(true);
  }

  return (
    <LayoutsKasir>
      <div className="mx-auto max-w-4xl">
        <ConfirmationPembelian
          isOpenConfPembelian={isOpenConfPembelian}
          closeModalConfPembelian={closeModalConfPembelian}
          handleSubmitPembelian={handleSubmitPembelian}
          loading={loading}
          formData={formData}
          harga_beli={harga_beli}
          selectImage={selectImage}
          namaKategori={kategori.find((k) => String(k.id) === String(kategoriId))?.nama_kategori || ''}
          namaJenis={jenisKategori.find((j) => String(j.id) === String(formData.jenis_kategori_id))?.nama_jenis || ''}
        />
        <OpenSearchTrxJTrxB
          setScanKode={setScanKode}
          isOpenModalSearch={isOpenModalSearch}
          closeModalSearch={closeModalSearch}
          scanKode={scanKode}
          dataScanKode={dataScanKode}
          loadingGetDetailScan={loadingGetDetailScan}
          statusKode={statusKode}
          msg={msg}
          numberWithCommas={numberWithCommas}
        />
        <CetakStrokePembelian dataPrintPembelian={dataPrintPembelian} isOpen={isOpen} closeModal={closeModal} />

        {/* Modal Pilih Karat */}
        {isOpenKaratModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-md shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col max-h-[85vh]">
              <div className="p-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 rounded-xl">
                    <IconDiamond size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 dark:text-slate-100 text-base">Pilih Kadar Karat</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Pilih tingkat kemurnian / karat perhiasan</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsOpenKaratModal(false)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                >
                  <IconX size={20} />
                </button>
              </div>

              {/* Grid Pilihan Karat */}
              <div className="overflow-y-auto p-4 max-h-[60vh]">
                <div className="grid grid-cols-3 gap-3">
                  {karatOption.map((item) => {
                    const val = typeof item === 'object' && item !== null ? item.karat : item;
                    const isSelected = Number(formData.karat) === Number(val);
                    return (
                      <button
                        key={val}
                        type="button"
                        onClick={() => {
                          setFormData((prev) => ({ ...prev, karat: Number(val) }));
                          setIsOpenKaratModal(false);
                        }}
                        className={`h-16 rounded-xl flex flex-col items-center justify-center gap-1 transition-all cursor-pointer ${
                          isSelected
                            ? 'bg-gradient-to-br from-amber-500 to-amber-600 text-white shadow-lg shadow-amber-500/30 ring-2 ring-amber-400 scale-[1.02]'
                            : 'bg-slate-50 dark:bg-slate-700/60 hover:bg-amber-50 dark:hover:bg-amber-950/40 text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-600 hover:border-amber-400'
                        }`}
                      >
                        <IconDiamond size={18} className={isSelected ? 'text-white' : 'text-amber-500'} />
                        <span className="text-sm font-extrabold leading-none">{val} K</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="p-3 border-t border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 flex justify-end">
                <button
                  type="button"
                  onClick={() => setIsOpenKaratModal(false)}
                  className="w-full py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 rounded-xl text-xs font-semibold transition cursor-pointer"
                >
                  Tutup
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Pilih Jenis Kategori */}
        {isOpenJenisModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-md shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col max-h-[80vh]">
              <div className="p-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50">
                <h3 className="font-bold text-slate-800 dark:text-slate-200">Pilih Jenis Kategori</h3>
                <button
                  type="button"
                  onClick={() => setIsOpenJenisModal(false)}
                  className="text-slate-400 hover:text-red-500 transition-colors"
                >
                  <IconX size={20} />
                </button>
              </div>
              <div className="p-4 border-b border-slate-100 dark:border-slate-700">
                <div className="relative">
                  <IconSearch size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Cari jenis kategori..."
                    value={searchJenis}
                    onChange={(e) => setSearchJenis(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                    autoFocus
                  />
                </div>
              </div>
              <div className="overflow-y-auto p-2">
                {jenisKategori.filter((item) => (item.nama_jenis || '').toLowerCase().includes(searchJenis.toLowerCase())).length > 0 ? (
                  jenisKategori
                    .filter((item) => (item.nama_jenis || '').toLowerCase().includes(searchJenis.toLowerCase()))
                    .map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => {
                          setFormData((prev) => ({ ...prev, jenis_kategori_id: item.id }));
                          setIsOpenJenisModal(false);
                          setSearchJenis('');
                        }}
                        className={`w-full text-left px-4 py-3 rounded-xl mb-1 transition-colors text-sm font-medium flex items-center justify-between ${
                          String(formData.jenis_kategori_id) === String(item.id)
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400'
                            : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                        }`}
                      >
                        {item.nama_jenis}
                        {String(formData.jenis_kategori_id) === String(item.id) && <div className="w-2 h-2 rounded-full bg-amber-500"></div>}
                      </button>
                    ))
                ) : (
                  <div className="text-center py-8 text-sm text-slate-500">Jenis kategori tidak ditemukan</div>
                )}
              </div>
            </div>
          </div>
        )}
        {loading ? (
          <div className="fixed top-0 left-0 right-0 bottom-0 w-full h-screen z-50 overflow-hidden bg-gray-700/20 opacity-75 flex flex-col items-center justify-center">
            <div className="w-12 h-12  rounded-full animate-spin border-4 border-dashed border-amber-500 border-t-transparent"></div>
          </div>
        ) : (
          ''
        )}
        <div className="bg-white dark:bg-slate-900 sm:shadow-sm sm:border border-slate-200 dark:border-slate-800 sm:rounded-xl">
          {/* Header */}
          <div className="px-4 py-4 sm:px-6 border-b border-slate-200 dark:border-slate-800">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Pembelian</h1>
                {/* <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  Catat transaksi pembelian barang
                </p> */}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setScannerOpen(true)}
                  className="h-10 w-10 sm:h-10 sm:w-auto sm:px-4 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white rounded-xl sm:rounded-lg text-sm font-semibold transition shadow-sm cursor-pointer"
                  title="Scan Surat (Mendukung QR Code & Barcode)"
                >
                  <div className="flex items-center -space-x-0.5">
                    <IconQrcode className="w-5 h-5 sm:w-4 sm:h-4" />
                    <IconBarcode className="w-5 h-5 sm:w-4 sm:h-4 text-emerald-200" />
                  </div>
                  <span className="hidden sm:inline">Scan Surat (QR & Barcode)</span>
                </button>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="h-10 w-10 sm:h-10 sm:w-auto sm:px-4 flex items-center justify-center gap-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 active:bg-slate-300 dark:active:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-xl sm:rounded-lg text-sm font-semibold transition"
                  title="Refresh halaman"
                >
                  <IconRefresh className="w-5 h-5 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Refresh</span>
                </button>
              </div>
            </div>
          </div>

          <div className="px-4 py-5 sm:p-6 pb-36 sm:pb-6">
            <ScannerModal
              isOpen={scannerOpen}
              onClose={() => setScannerOpen(false)}
              onScanSuccess={lookupScannedInvoice}
            />

            {scannedTrxDetails && (
              <div className="mb-6 bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200/60 dark:border-emerald-800/45 rounded-xl p-3 flex items-center justify-between text-emerald-800 dark:text-emerald-400 shadow-sm transition-all duration-300">
                <div className="flex items-center gap-2.5">
                  <div className="relative flex h-3 w-3 flex-shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
                  </div>
                  <div className="text-sm">
                    <span className="font-extrabold font-mono text-emerald-700 dark:text-emerald-350">{scannedTrxDetails.kode_trx}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setIsDetailModalOpen(true)}
                    className="h-8 w-8 flex items-center justify-center rounded-lg bg-emerald-100/60 dark:bg-emerald-900/20 hover:bg-emerald-200/70 dark:hover:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-800/30 active:scale-95 transition-all"
                    title="Lihat Detail Scan"
                  >
                    <IconEye size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      resetForm();
                      setIsReadOnlyKodeItem(true);
                      toast.info('Formulir di-reset ke pengaturan awal.');
                    }}
                    className="h-8 w-8 flex items-center justify-center rounded-lg bg-red-50 hover:bg-red-100 dark:bg-red-950/30 dark:hover:bg-red-950/50 text-red-650 dark:text-red-400 border border-red-200 dark:border-red-900/30 active:scale-95 transition-all"
                    title="Batal / Reset Scan"
                  >
                    <IconTrash size={16} />
                  </button>
                </div>
              </div>
            )}

            <DetailTrxModal
              isOpen={isDetailModalOpen}
              onClose={() => setIsDetailModalOpen(false)}
              trx={scannedTrxDetails}
              numberWithCommas={numberWithCommas}
            />

            <form ref={formRef} className="space-y-6">
              {/* === Section: Kode Item === */}
              <div className="relative">
                <div className="flex gap-2">
                  <input
                    placeholder="Masukkan Kode Item"
                    type="text"
                    id="kode_item"
                    name="kode_item"
                    readOnly={formData.surat === '1' && isReadOnlyKodeItem}
                    autoComplete="off"
                    value={formData.kode_item}
                    onChange={handleOnChange}
                    className="flex-1 h-14 sm:h-12 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 pt-4 pb-1 sm:pt-3.5 sm:pb-1.5 text-base sm:text-sm dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 read-only:bg-slate-100 dark:read-only:bg-slate-900/50 read-only:cursor-not-allowed"
                  />
                  {formData.surat === '1' && isReadOnlyKodeItem && !scannedTrxDetails && (
                    <button
                      type="button"
                      onClick={() => {
                        setIsReadOnlyKodeItem(false);
                        toast.info('Input Kode Item dibuka untuk pengetikan manual.');
                      }}
                      className="px-3.5 text-xs bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-700 dark:text-slate-300 font-bold transition flex items-center justify-center whitespace-nowrap active:scale-95"
                      title="Klik jika QR rusak / tidak bisa scan"
                    >
                      QR Rusak?
                    </button>
                  )}
                </div>
                <label className="absolute -top-2.5 left-3 bg-white dark:bg-slate-900 px-1.5 text-xs font-medium text-slate-500 dark:text-slate-400">Kode Item (Opsional)</label>
              </div>

              {/* === Section: Kategori === */}
              <div className="space-y-5">
                <div className="relative">
                  <select
                    value={kategoriId}
                    onChange={(e) => {
                      if (e.target.value) {
                        SelectJenisKategori(e.target.value);
                        setFormData((prev) => ({ ...prev, jenis_kategori_id: '' }));
                      } else {
                        setKategoriId('');
                        setJenisKategori([]);
                        setFormData((prev) => ({ ...prev, jenis_kategori_id: '' }));
                      }
                    }}
                    required
                    className="w-full h-14 sm:h-12 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 pt-4 pb-1 sm:pt-3.5 sm:pb-1.5 text-base sm:text-sm dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 cursor-pointer appearance-none"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
                  >
                    <option value="">Pilih Kategori</option>
                    {kategori.map((item) => (
                      <option key={item.id} value={item.id}>{item.nama_kategori}</option>
                    ))}
                  </select>
                  <label className="absolute -top-2.5 left-3 bg-white dark:bg-slate-900 px-1.5 text-xs font-medium text-slate-500 dark:text-slate-400">Kategori</label>
                </div>

                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsOpenJenisModal(true)}
                    disabled={!kategoriId}
                    className="w-full h-14 sm:h-12 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 pt-4 pb-1 sm:pt-3.5 sm:pb-1.5 text-base sm:text-sm dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 cursor-pointer disabled:bg-slate-50 dark:disabled:bg-slate-900 disabled:text-slate-400 dark:disabled:text-slate-600 disabled:cursor-not-allowed flex items-center justify-between text-left"
                  >
                    <span className="truncate">
                      {jenis_kategori_id
                        ? jenisKategori.find((j) => String(j.id) === String(jenis_kategori_id))?.nama_jenis || 'Pilih Jenis Kategori'
                        : 'Pilih Jenis Kategori'}
                    </span>
                    <IconSearch size={16} className="text-slate-400 shrink-0" />
                  </button>
                  <label className="absolute -top-2.5 left-3 bg-white dark:bg-slate-900 px-1.5 text-xs font-medium text-slate-500 dark:text-slate-400">Jenis Kategori</label>
                </div>
              </div>

              {/* === Section: Kondisi & Surat === */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="relative">
                  <select
                    value={formData.kondisi_barang}
                    onChange={(e) => setFormData({ ...formData, kondisi_barang: e.target.value })}
                    required
                    className="w-full h-14 sm:h-12 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 pt-4 pb-1 sm:pt-3.5 sm:pb-1.5 text-base sm:text-sm dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 cursor-pointer appearance-none"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
                  >
                    <option value="">Pilih</option>
                    {kondisiOption.map((item) => (
                      <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                  </select>
                  <label className="absolute -top-2.5 left-3 bg-white dark:bg-slate-900 px-1.5 text-xs font-medium text-slate-500 dark:text-slate-400">Kondisi</label>
                </div>
                <div className="relative">
                  <select
                    value={formData.surat}
                    onChange={(e) => {
                      const newSurat = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        surat: newSurat,
                        kode_item: newSurat === '0' ? '-' : prev.kode_item === '-' ? '' : prev.kode_item
                      }));
                      if (newSurat === '0') {
                        setIsReadOnlyKodeItem(false);
                      } else {
                        setIsReadOnlyKodeItem(true);
                      }
                    }}
                    className="w-full h-14 sm:h-12 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 pt-4 pb-1 sm:pt-3.5 sm:pb-1.5 text-base sm:text-sm dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 cursor-pointer appearance-none"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
                  >
                    <option value="">Pilih</option>
                    {suratOption.map((item) => (
                      <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                  </select>
                  <label className="absolute -top-2.5 left-3 bg-white dark:bg-slate-900 px-1.5 text-xs font-medium text-slate-500 dark:text-slate-400">Surat</label>
                </div>
              </div>

              {/* === Section: Berat & Karat === */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="relative">
                  <input
                    placeholder='Contoh: 2.350'
                    type="number"
                    step="0.001"
                    inputMode="decimal"
                    id="berat"
                    name="berat"
                    required
                    autoComplete="off"
                    value={formData.berat}
                    onChange={handleOnChange}
                    className="w-full h-14 sm:h-12 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 pt-4 pb-1 sm:pt-3.5 sm:pb-1.5 text-base sm:text-sm dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
                  />
                  <label className="absolute -top-2.5 left-3 bg-white dark:bg-slate-900 px-1.5 text-xs font-medium text-slate-500 dark:text-slate-400">Berat (gram)</label>
                </div>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsOpenKaratModal(true)}
                    className="w-full h-14 sm:h-12 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 pt-4 pb-1 sm:pt-3.5 sm:pb-1.5 text-base sm:text-sm dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 cursor-pointer flex items-center justify-between text-left transition-all hover:border-amber-400 group"
                  >
                    <span className={`truncate ${formData.karat !== '' ? 'font-bold text-slate-800 dark:text-slate-100' : 'text-slate-400 dark:text-slate-500 font-normal'}`}>
                      {formData.karat !== '' ? `${formData.karat} Karat` : 'Pilih Karat'}
                    </span>
                    <IconDiamond size={18} className="text-amber-500 shrink-0 ml-1 group-hover:scale-110 transition-transform" />
                  </button>
                  <label className="absolute -top-2.5 left-3 bg-white dark:bg-slate-900 px-1.5 text-xs font-medium text-slate-500 dark:text-slate-400">Karat</label>
                </div>
              </div>

              {/* === Section: Harga === */}
              <div className="relative">
                <CurrencyInput
                  name="harga_beli"
                  value={harga_beli}
                  autoComplete="off"
                  onChange={(value) => setHargaBeli(() => value)}
                  className="w-full h-14 sm:h-12 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 pt-4 pb-1 sm:pt-3.5 sm:pb-1.5 text-base sm:text-sm dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
                />
                <label className="absolute -top-2.5 left-3 bg-white dark:bg-slate-900 px-1.5 text-xs font-medium text-slate-500 dark:text-slate-400">Total Harga Beli</label>
              </div>

              {/* === Section: Keterangan === */}
              <div className="relative">
                <textarea
                  id="message"
                  value={formData.keterangan}
                  onChange={handleOnChange}
                  rows={3}
                  name="keterangan"
                  className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 pt-4 pb-2 sm:pt-3.5 sm:pb-2.5 text-base sm:text-sm dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
                  placeholder="Catatan tambahan (bila ada)..."
                />
                <label className="absolute -top-2.5 left-3 bg-white dark:bg-slate-900 px-1.5 text-xs font-medium text-slate-500 dark:text-slate-400">Keterangan (Opsional)</label>
              </div>

              {/* === Section: Foto === */}
              <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800 p-4 sm:p-5 rounded-lg">
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">Foto Barang & Surat</p>
                {/* <p className="text-xs text-slate-400 mt-0.5">Format: JPG/PNG/WEBP</p> */}

                {selectImage.length > 0 && (
                  <div className="mt-4 flex items-center gap-3 overflow-x-auto pb-2">
                    {selectImage.map((image, index) => (
                      <div key={index} className="relative flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => setSelectImage(selectImage.filter((e) => e !== image))}
                          className="absolute -top-2 -right-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full p-1.5 shadow-md z-10 active:scale-90 transition-transform"
                        >
                          <IconX width={16} className="dark:text-slate-200" />
                        </button>
                        <PhotoProvider>
                          <PhotoView src={image}>
                            <img
                              className="h-24 w-24 sm:h-20 sm:w-20 rounded-lg border border-slate-200 dark:border-slate-700 object-cover bg-white dark:bg-slate-900 cursor-pointer"
                              src={image}
                              alt="Preview"
                              title="Klik untuk memperbesar gambar"
                            />
                          </PhotoView>
                        </PhotoProvider>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-4 grid grid-cols-2 gap-3">
                  <label className="flex items-center justify-center gap-2 h-14 sm:h-11 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-2 border-amber-200 dark:border-amber-800/50 hover:bg-amber-100 dark:hover:bg-amber-900/30 active:bg-amber-200 dark:active:bg-amber-900/40 text-sm font-semibold cursor-pointer transition rounded-lg">
                    <IconCamera className="w-5 h-5" />
                    Kamera
                    <input
                      type="file"
                      onChange={handleImageChange}
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                    />
                  </label>
                  <label className="flex items-center justify-center gap-2 h-14 sm:h-11 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-2 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 active:bg-slate-100 dark:active:bg-slate-600 text-sm font-semibold cursor-pointer transition rounded-lg">
                    <IconPhoto className="w-5 h-5" />
                    Galeri
                    <input
                      type="file"
                      onChange={handleImageChange}
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              <div className="fixed sm:static bottom-0 left-0 right-0 p-4 sm:p-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sm:bg-transparent border-t sm:border-t-0 border-slate-200 dark:border-slate-800/80 sm:border-transparent shadow-[0_-4px_12px_rgba(0,0,0,0.05)] sm:shadow-none z-40 sm:z-auto">
                <div className="max-w-4xl mx-auto">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      openModalConfPembelian();
                    }}
                    disabled={loading || !isFormValid}
                    className="w-full h-12 sm:h-11 px-8 text-base sm:text-sm font-bold text-white bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 active:from-amber-600 active:to-amber-700 rounded-xl sm:rounded-lg shadow-lg shadow-amber-500/20 disabled:from-slate-300 disabled:to-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed disabled:shadow-none transition-all flex items-center justify-center gap-2"
                  >
                    <IconDeviceFloppy className="w-5 h-5 sm:w-4 sm:h-4" />
                    {loading ? 'Proses...' : 'Simpan Transaksi'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </LayoutsKasir>
  );
}
